import React, { useEffect, useState } from "react";
import { Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/auth";
import { Btn, Pill, inputCls, when } from "./kit";
import { ResidentEditDialog } from "./ResidentEditDialog";
import { LoginPinButton, PinStatus, useLoginPinRefresh } from "./LoginPin";
import { LeaderBadge, ResidentLeaderControls } from "./ResidentLeader";
import { TeaPhoneTag } from "./PreviewAs";

export const ResidentsTab = ({ meta }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = () => apiClient.get("/console/residents").then(({ data }) => {
    setRows(data.residents);
    setEdit((cur) => cur && data.residents.find((r) => r.id === cur.id));
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  useLoginPinRefresh(load);
  const shown = rows.filter((r) => `${r.first_name} ${r.last_name} ${r.preferred_name || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="console-residents-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-white/60">{rows.length} residents · {rows.filter((r) => r.portal_active).length} with an active portal link · <span data-testid="residents-leader-count">{(() => { const n = rows.filter((r) => r.resident_leader).length; return `${n} Resident Leader${n === 1 ? "" : "s"}`; })()}</span></p>
        <div className="relative w-64"><Search size={13} className="absolute left-3 top-3 text-white/40" />
          <input data-testid="residents-search" className={`${inputCls} pl-8`} placeholder="Search residents" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      </div>
      <div className="overflow-x-auto border border-white/10 rounded-sm">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b border-white/10 font-mono text-[10px] tracking-[0.18em] uppercase text-white/50">
            <th className="p-3">Resident</th><th className="p-3">Status</th><th className="p-3">Phase</th><th className="p-3">Portal</th><th className="p-3">PIN</th><th className="p-3" />
          </tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} data-testid={`resident-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <td className="p-3 text-white">{r.first_name} {r.last_name}{r.preferred_name && <span className="text-white/40"> “{r.preferred_name}”</span>}{r.resident_leader && <span className="ml-2"><LeaderBadge /></span>}{r.tea_phone && <span className="ml-2"><TeaPhoneTag id={r.id} /></span>}</td>
                <td className="p-3"><Pill v={r.status} /></td>
                <td className="p-3 font-mono text-[11px] text-white/70">{r.current_phase || "—"}</td>
                <td className="p-3 text-[11px] text-white/60">{r.portal_active ? `active · used ${when(r.portal_last_used_at)}` : "—"}</td>
                <td className="p-3 text-[11px]"><PinStatus row={r} testId={`resident-pin-${r.id}`} /></td>
                <td className="p-3 text-right whitespace-nowrap align-top"><ResidentLeaderControls rid={r.id} name={`${r.first_name} ${r.last_name}`} leader={!!r.resident_leader} onChanged={load} compact /><span className="mr-2" /><LoginPinButton kind="resident" id={r.id} name={`${r.first_name} ${r.last_name}`} status={r.status} source="console_users_resident" className="mr-2" /><Btn tone="muted" data-testid={`resident-edit-${r.id}`} onClick={() => setEdit(r)}><Pencil size={12} /> Edit</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <ResidentEditDialog key={edit.id} resident={edit} meta={meta} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
};
